/*
 * QR Waiter-Call API client.
 *
 * Public endpoints used by the QR menu "Ofisanti çağır" feature. Refer to
 * docs/QR_WAITER_CALL.md for the full backend contract — the types below
 * mirror the response shapes 1-to-1.
 *
 * Endpoints (no auth — secured by the QR's UUID + a 60-second dedup window):
 *   POST   /api/qr/{uniqueUrl}/call-waiter
 *   GET    /api/qr/{uniqueUrl}/call-waiter/active
 *   DELETE /api/qr/{uniqueUrl}/call-waiter/{callId}
 *
 * Throttle (per spec):
 *   - POST   10/min
 *   - GET    60/min  (cheap polling, but we prefer WebSocket via the hook)
 *   - DELETE 10/min
 */

import axios from 'axios';
import { base_url } from './index';

// ---------- Domain types ----------

/**
 * The 6 canonical reason codes the backend understands. Anything else is
 * accepted but echoed back with `reason_label=null`, so we keep the type
 * permissive (string) while exposing the canonical list as an enum-like
 * tuple for the picker UI.
 */
export const QR_WAITER_REASON_CODES = [
  'water',
  'bill',
  'help',
  'cleanup',
  'menu',
  'other',
] as const;
export type QrWaiterReasonCode = typeof QR_WAITER_REASON_CODES[number];

export type QrWaiterCallStatus = 'pending' | 'acknowledged' | 'resolved';

export interface QrWaiterAssignedStaff {
  id: number;
  name: string;
}

/** Shape returned by `POST /call-waiter` (covers both new + already_pending). */
export interface QrWaiterCallCreateResponse {
  call_id: number;
  status: QrWaiterCallStatus;
  already_pending: boolean;
  assigned_waiter: QrWaiterAssignedStaff | null;
  reason_code: string | null;
  reason_label: string | null;
  note: string | null;
  created_at: string;
  message: string;
}

/** Shape returned by `GET /call-waiter/active` (the inner `call` field). */
export interface QrWaiterActiveCall {
  call_id: number;
  status: QrWaiterCallStatus;
  reason_code: string | null;
  reason_label: string | null;
  note: string | null;
  assigned_waiter: QrWaiterAssignedStaff | null;
  created_at: string;
  acknowledged_at: string | null;
}

/** Wrapped response so we can disambiguate "no active call" from a 404. */
export interface QrWaiterActiveCallResponse {
  call: QrWaiterActiveCall | null;
}

/** Backend response for status-changed websocket events. */
export interface QrWaiterCallStatusChangedEvent {
  call_id: number;
  table_id: number;
  assigned_user_id: number | null;
  status: QrWaiterCallStatus;
  acknowledged_by?: number | null;
  acknowledged_by_name?: string | null;
  occurred_at: string;
}

/** Backend response for the `.waiter.called` event (POS dashboard). */
export interface QrWaiterCalledEvent {
  call_id: number;
  table_id: number;
  table_name: string;
  assigned_user_id: number | null;
  assigned_user_name: string | null;
  reason_code: string | null;
  reason_label: string | null;
  note: string | null;
  status: QrWaiterCallStatus;
  created_at: string;
}

// ---------- HTTP client ----------

// Public endpoints — no Authorization header. Mirrors qrAdisyon.ts so future
// retry/timeout tweaks live in a single place.
const qrClient = axios.create({
  baseURL: base_url,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 12000,
});

// ---------- API calls ----------

/**
 * Create a new waiter call (or return the still-active one if the customer
 * is mashing the button — backend dedup window is 60s).
 */
export async function createQrWaiterCall(
  uniqueUrl: string,
  payload: { reason_code?: QrWaiterReasonCode | string | null; note?: string | null } = {},
  signal?: AbortSignal,
): Promise<QrWaiterCallCreateResponse> {
  const body: Record<string, string> = {};
  if (payload.reason_code) body.reason_code = payload.reason_code;
  if (payload.note && payload.note.trim()) body.note = payload.note.trim();
  const res = await qrClient.post<QrWaiterCallCreateResponse>(
    `/qr/${encodeURIComponent(uniqueUrl)}/call-waiter`,
    body,
    { signal },
  );
  return res.data;
}

/** Fetch the currently-active call for this QR table (used on page reload). */
export async function getQrActiveWaiterCall(
  uniqueUrl: string,
  signal?: AbortSignal,
): Promise<QrWaiterActiveCallResponse> {
  const res = await qrClient.get<QrWaiterActiveCallResponse>(
    `/qr/${encodeURIComponent(uniqueUrl)}/call-waiter/active`,
    { signal },
  );
  return res.data;
}

/** Customer-initiated "I tapped by mistake" cancel. */
export async function cancelQrWaiterCall(
  uniqueUrl: string,
  callId: number,
  signal?: AbortSignal,
): Promise<{ message: string }> {
  const res = await qrClient.delete<{ message: string }>(
    `/qr/${encodeURIComponent(uniqueUrl)}/call-waiter/${callId}`,
    { signal },
  );
  return res.data;
}
